import { api } from './api'

export type DisplayMode = 'medication_select' | 'protocol_showcase'

export interface ClinicProgram {
  id: string
  clinic_id: string
  source_program_id?: string
  title: string
  subtitle: string
  inclusions: string
  target: string
  image_key?: string
  image_url?: string
  display_mode?: DisplayMode
  protocol_label?: string
  is_deleted: boolean
  created_at: string
  updated_at: string
  program_templates?: { title: string; pmk_template_id: string }[]
  program_products: { id: string; product_id: string; product?: { id: string; description: string } }[]
  default_medications?: ClinicProgramDefaultMedication[]
}

export interface ClinicProgramDefaultMedication {
  id: string
  clinic_program_id: string
  medication_id: string
  medication_name: string
  dose: string
  sig: string
  quantity: number
  days_supply: string
  refills_authorized: number
  product_id?: string
  unit_of_measure?: string
  is_deleted: boolean
  created_at: string
  updated_at: string
}

export interface ProgramMedication {
  id: string
  name: string
  doses: string[]
  sig: string[]
  unit_of_measure?: string
  template_id: string
  template_title: string
  map_dose_to_product: { dose: string; product_id: string; base_price?: number; price?: number }[]
}

export interface MedicationTemplate {
  id: string
  title: string
}

export interface AvailableProduct {
  id: string
  description: string
  category?: string
  status?: string
  image_url?: string
}

export interface PrescriptionProduct {
  key: string
  medication_name: string
  dose: string
  product_id: string | null
  pharmetika_product_id: string | null
  unit_of_measure: string | null
  default_sigs: string[]
  qty_options: number[]
  base_price: number
  price: number
  compounding_reason: string | null
  template_id: string
  template_title: string
  program_id: string | null
  program_title: string | null
}

interface PaginatedResponse {
  programs: ClinicProgram[]
  pagination: {
    currentPage: number
    totalPages: number
    totalItems: number
    itemsPerPage: number
    hasNextPage: boolean
    hasPrevPage: boolean
  }
}

class ClinicProgramService {
  async getPrograms(
    clinicId: string,
    params?: { page?: number; limit?: number; search?: string; sortBy?: string; sortOrder?: string }
  ): Promise<PaginatedResponse> {
    const queryParams = new URLSearchParams()
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.search) queryParams.append('search', params.search)
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy)
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder)
    const response = await api.get(`/clinic/${clinicId}/programs?${queryParams.toString()}`)
    return response.data
  }

  async createProgram(
    clinicId: string,
    data: {
      title: string
      subtitle: string
      inclusions: string
      target: string
      image_key?: string
      display_mode?: DisplayMode
    }
  ): Promise<ClinicProgram> {
    const response = await api.post(`/clinic/${clinicId}/programs`, data)
    return response.data
  }

  async updateProgram(
    clinicId: string,
    programId: string,
    data: {
      title?: string
      subtitle?: string
      inclusions?: string
      target?: string
      image_key?: string
      display_mode?: DisplayMode
    }
  ): Promise<ClinicProgram> {
    const response = await api.put(`/clinic/${clinicId}/programs/${programId}`, data)
    return response.data
  }

  async deleteProgram(clinicId: string, programId: string): Promise<void> {
    await api.delete(`/clinic/${clinicId}/programs/${programId}`)
  }

  async updateImage(clinicId: string, programId: string, imageKey: string | null): Promise<ClinicProgram> {
    const response = await api.put(`/clinic/${clinicId}/programs/${programId}/image`, { image_key: imageKey })
    return response.data
  }

  async assignMedicationTemplate(clinicId: string, programId: string, medicationTemplateIds: string[]): Promise<void> {
    await api.post(`/clinic/${clinicId}/programs/${programId}/assign-medication-template`, { medicationTemplateIds })
  }

  async getMedicationTemplates(clinicId: string): Promise<MedicationTemplate[]> {
    const response = await api.get(`/clinic/${clinicId}/programs/medication-templates`)
    return response.data || []
  }

  async assignProducts(clinicId: string, programId: string, productIds: string[]): Promise<void> {
    await api.post(`/clinic/${clinicId}/programs/${programId}/assign-products`, { productIds })
  }

  async getAvailableProducts(clinicId: string): Promise<AvailableProduct[]> {
    const response = await api.get(`/clinic/${clinicId}/programs/available-products`)
    return response.data || []
  }

  async getProgramMedications(
    clinicId: string,
    programId: string,
    patientId?: string
  ): Promise<{ medications: ProgramMedication[] }> {
    const queryParams = new URLSearchParams()
    if (patientId) queryParams.append('patientId', patientId)
    const query = queryParams.toString()
    const response = await api.get(
      `/clinic/${clinicId}/programs/${programId}/medications${query ? `?${query}` : ''}`
    )
    return response.data || { medications: [] }
  }

  async getDefaultMedications(clinicId: string, programId: string): Promise<{ defaultMedications: ClinicProgramDefaultMedication[] }> {
    const response = await api.get(`/clinic/${clinicId}/programs/${programId}/default-medications`)
    return response.data || { defaultMedications: [] }
  }

  async saveDefaultMedications(
    clinicId: string,
    programId: string,
    medications: Omit<ClinicProgramDefaultMedication, 'id' | 'clinic_program_id' | 'is_deleted' | 'created_at' | 'updated_at'>[]
  ): Promise<{ defaultMedications: ClinicProgramDefaultMedication[] }> {
    const response = await api.post(`/clinic/${clinicId}/programs/${programId}/default-medications`, { medications })
    return response.data || { defaultMedications: [] }
  }

  async deleteDefaultMedication(clinicId: string, programId: string, medicationId: string): Promise<void> {
    await api.delete(`/clinic/${clinicId}/programs/${programId}/default-medications/${medicationId}`)
  }

  async getPrescriptionProducts(
    clinicId: string,
    patientId?: string
  ): Promise<{ products: PrescriptionProduct[] }> {
    const queryParams = new URLSearchParams()
    if (patientId) queryParams.append('patientId', patientId)
    const query = queryParams.toString()
    const response: any = await api.get(
      `/clinic/${clinicId}/programs/prescription-products${query ? `?${query}` : ''}`
    )
    // api.get returns axios response.data = { success, data: { products }, message }
    const products = response?.data?.products || response?.products || []
    return { products }
  }
}

export const clinicProgramService = new ClinicProgramService()
